import React, { useState } from 'react';
import { Popover, Button, Modal, Text } from '@shopify/polaris';

/**
 * Saved Views dropdown — Polaris-styled, alphabetical list of the user's
 * saved views. Each row:
 *   - star icon (filled = default, empty = not)
 *   - view name (click loads the view)
 *   - trash icon (delete; opens confirm modal)
 *
 * Hidden entirely when there are no saved views.
 */
export default function SavedViewsDropdown({
  views,
  onLoad,
  onSetDefault,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // { id, name }

  if (!views || views.length === 0) return null;

  const handleLoad = (v) => {
    onLoad(v);
    setOpen(false);
  };

  const handleStar = (e, v) => {
    e.stopPropagation();
    onSetDefault(v);
  };

  const handleTrash = (e, v) => {
    e.stopPropagation();
    setConfirmDel({ id: v.id, name: v.name });
  };

  const confirmDelete = async () => {
    if (!confirmDel) return;
    await onDelete(confirmDel.id);
    setConfirmDel(null);
  };

  return (
    <>
      <Popover
        active={open}
        activator={
          <Button size="slim" disclosure onClick={() => setOpen(v => !v)}>
            Saved Views
          </Button>
        }
        onClose={() => setOpen(false)}
        preferredAlignment="left"
      >
        <div className="otsv-list" role="menu">
          {views.map(v => (
            <div
              key={v.id}
              className="otsv-row"
              onClick={() => handleLoad(v)}
              role="menuitem"
              tabIndex={0}
            >
              <button
                type="button"
                className={`otsv-star${v.is_default ? ' otsv-star--on' : ''}`}
                onClick={e => handleStar(e, v)}
                aria-label={v.is_default ? 'Unset as default' : 'Set as default'}
                title={v.is_default ? 'Default — click to unset' : 'Set as default'}
              >
                {v.is_default ? '★' : '☆'}
              </button>
              <span className="otsv-name" title={v.name}>{v.name}</span>
              <button
                type="button"
                className="otsv-trash"
                onClick={e => handleTrash(e, v)}
                aria-label="Delete saved view"
                title="Delete"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      </Popover>

      {confirmDel && (
        <Modal
          open
          onClose={() => setConfirmDel(null)}
          title="Delete saved view?"
          primaryAction={{
            content: 'Delete',
            destructive: true,
            onAction: confirmDelete,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setConfirmDel(null) }]}
        >
          <Modal.Section>
            <Text as="p">
              Delete saved view <strong>{confirmDel.name}</strong>? This can't be undone.
            </Text>
          </Modal.Section>
        </Modal>
      )}
    </>
  );
}
